
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { getHeaders } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { createEmbeddingContext, EmbeddingContext } from 'amazon-quicksight-embedding-sdk';
import { fetchProperties } from "@/api/property";
import dayjs from "dayjs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

type embedUrlResponseType = {
  EmbedUrl: string,
  Status: number,
}

export const getUrl = async (dashboardId: string): Promise<string> => {
  const headers = await getHeaders();

  try {
    const fetchedPost: any = await fetch(`${import.meta.env.VITE_API_URL}/analytics/getEmbedUrl/${dashboardId}`, {
      mode: 'cors',
      method: 'get',
      headers: headers,
    });
    
    const data: embedUrlResponseType = await fetchedPost.json();
    console.log("embed url ", data);
   
    return data.EmbedUrl;
  } catch (error) {
    console.error("Error:", error);
    return "Error getting analytics url";
  }
};

const ShowAnalytics = (): JSX.Element => {
  const [analyticsUrl, setAnalyticsUrl] = useState<string>('');
  const { dashboardId } = useParams();
  console.log("🚀 ~ ShowAnalytics ~ dashboardId:", dashboardId);
  const [loading, setLoading] = useState<boolean>(true);
  console.log("🚀 ~ ShowAnalytics ~ loading:", loading);
  const [propertyId, setPropertyId] = useState<string>('');
  const [propertyData, setPropertyData] = useState<any>([]);
  const [embeddedDashboardExperience, setEmbeddedDashboardExperience] = useState<any>(null);

  const previousMonday = dayjs().subtract(1, 'week').startOf('week').add(1, 'day');
  const previousSunday = dayjs().subtract(1, 'week').startOf('week').add(7, 'day');
  const [startDate, setStartDate] = useState<Date | undefined>(previousMonday.toDate());
  const [endDate, setEndDate] = useState<Date | undefined>(previousSunday.toDate());
  const [dashboardData, setDashboardData] = useState<any>([]);
  const [period, setPeriod] = useState<string>('Week');

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      //Load Property Select
      let initPropertyId = '';
      
      const data = await fetchProperties();
      console.log("🚀 ~ fetchData ~ data:", data);
      setPropertyData(data);
      setPropertyId(data[0].name);
      initPropertyId = data[0].name;

      setDashboardData(dashboardId);
      const result = await getUrl(dashboardId);
      if (result) {
        console.log("result: ", result);
        setAnalyticsUrl(result.toString());

        const frameOptions = {
          url: result.toString(),
          container: '#experience-container',
          resizeHeightOnSizeChangedEvent: true,
          onChange: (changeEvent: { eventName: any; }, metadata: any) => {
              switch (changeEvent.eventName) {
                  case 'FRAME_MOUNTED': {
                      console.log("Do something when the experience frame is mounted.");
                      break;
                  }
                  case 'FRAME_LOADED': {
                      console.log("Do something when the experience frame is loaded.");
                      break;
                  }
              }
          },
        };

        const formattedStartDate = dayjs(startDate).format('YYYY/MM/DD');
        const formattedEndDate = dayjs(endDate).format('YYYY/MM/DD');

        const contentOptions = {
          parameters: [
            {
                Name: 'granularity',
                Values: ['Week'],
            },
            {   
              Name: 'propertyName', 
              Values: [initPropertyId] 
            },
            {
              Name: 'startDate',
              Values: [formattedStartDate]
            },
            {
              Name: 'endDate',
              Values: [formattedEndDate]
            },
            {
              Name: 'granularity',
              Values: [period]
            },
          ],
        };

        const getEmbeddingContext = async (): Promise<EmbeddingContext> => {
            const embeddingContext = await createEmbeddingContext();
            return embeddingContext;
        }

        let embeddingContext: EmbeddingContext;

        getEmbeddingContext().then(context => {
            embeddingContext = context;

            const {
                embedDashboard,
            } = embeddingContext;

         
            // Embedding a dashboard experience
            embedDashboard(frameOptions, contentOptions).then(embeddedDashboardExperience => {
                setEmbeddedDashboardExperience(embeddedDashboardExperience);
            });
        });
      }

      setLoading(false);
    };
    fetchData();
  }, []);

  function handlePropertyChange(value: string): void {
    setPropertyId(value);
    embeddedDashboardExperience.setParameters([
      {
          Name: 'propertyName',
          Values: [value]
      }
    ]);
  }

  function handleDashboardChange(value: string): void {
    setDashboardData(value);

    const dashboardId = value;
    const formattedStartDate = dayjs(startDate).format('YYYY/MM/DD');
    const formattedEndDate = dayjs(endDate).format('YYYY/MM/DD');
    const options = {
        parameters: [
            {
                Name: 'propertyName',
                Values: [propertyId],
            },
            {
                Name: 'granularity',
                Values: [period],
            },
            {
                Name: 'startDate',
                Values: [formattedStartDate]
            },
            {
                Name: 'endDate',
                Values: [formattedEndDate]
            },
        ]
    };
    embeddedDashboardExperience.navigateToDashboard(dashboardId, options);
  }

  function resetDates(){
    const formattedStartDate = dayjs(startDate).format('YYYY/MM/DD');
    const formattedEndDate = dayjs(endDate).format('YYYY/MM/DD');
    embeddedDashboardExperience.setParameters([
      {
          Name: 'startDate',
          Values: [formattedStartDate]
      },
      {
        Name: 'endDate',
        Values: [formattedEndDate]
      },
    ]);
  }

  function handlePeriodChange(value: string): void {
    setPeriod(value);
    embeddedDashboardExperience.setParameters([
      {
          Name: 'granularity',
          Values: [value]
      }
    ]);
  }

  return (
    <div className="container mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="py-1">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div>
                  <Select 
                    value={propertyId} 
                    onValueChange={handlePropertyChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Property" />
                    </SelectTrigger>
                    <SelectContent>
                      {propertyData && propertyData.map((item: any, key: number) => (
                        <SelectItem key={key} value={item.name}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select 
                    value={dashboardData} 
                    onValueChange={handleDashboardChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Dashboard" />
                    </SelectTrigger>
                    <SelectContent>
                      {JSON.parse(sessionStorage.getItem("dashboards") || "[]").map((item: any, key: number) => (
                        <SelectItem key={key} value={item.id}>{item.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Select 
                    value={period} 
                    onValueChange={handlePeriodChange}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Period" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Day">Day</SelectItem>
                      <SelectItem value="Week">Week</SelectItem>
                      <SelectItem value="Month">Month</SelectItem>
                      <SelectItem value="Quarter">Quarter</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        <div className="py-1">
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PP") : <span>From Date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={setStartDate}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PP") : <span>To Date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={setEndDate}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-center justify-center">
                  <Button onClick={resetDates} variant="default">
                    Apply Dates
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      <div id="experience-container" className="w-full h-[calc(100vh-240px)]"></div>
    </div>
  );
};

export default ShowAnalytics;
